import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Search, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import { motion, AnimatePresence } from "framer-motion";
import logoImg from "@/assets/logo-transparent.png";
import { searchSite, SearchResult } from "@/lib/searchIndex";

const navLinks = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Packages", to: "/packages" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Blog", to: "/blog" },
  { label: "Contact Us", to: "/contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();

  const searchResults = useMemo(() => searchSite(searchQuery), [searchQuery]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen || searchOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen, searchOpen]);

  const handleResultClick = (url: string) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(url);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    setMobileOpen(false);
    if (window.location.pathname === "/") {
      e.preventDefault();
      window.location.reload();
    }
  };

  const LogoBrand = ({ className = "" }: { className?: string }) => (
    <Link to="/" className={`flex flex-col items-center gap-0 ${className}`} onClick={handleLogoClick}>
      <div className="h-20 w-20">
        <img src={logoImg} alt="MVIBE Logo" className="h-full w-full object-contain" />
      </div>
      <span className="text-[13px] tracking-[0.15em] text-secondary font-sans leading-none -mt-3">
        Clarity Within. Success Beyond.
      </span>
    </Link>
  );

  return (
    <>
      <header
        className={`sticky top-0 z-40 w-full transition-all duration-300 ${
          scrolled
            ? "bg-card/90 backdrop-blur-md shadow-sm"
            : "bg-card"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between h-24 px-4 lg:px-8">
          <LogoBrand />

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-0.5">
            {navLinks.slice(0, 2).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={l.to === "/" ? handleLogoClick : undefined}
                className="px-2.5 py-2 text-sm lg:text-base font-medium text-primary hover:text-primary/70 transition-colors"
              >
                {l.label}
              </Link>
            ))}

            {/* Services with dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services"
                className="flex items-center gap-1 px-2.5 py-2 text-sm lg:text-base font-medium text-primary hover:text-primary/70 transition-colors"
              >
                Services
                <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </Link>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-1 w-[420px] bg-card rounded-lg shadow-lg border border-border p-3 grid grid-cols-3 gap-3"
                  >
                    <Link
                      to="/services/individuals"
                      className="group p-2.5 rounded-md hover:bg-muted transition-colors"
                      onClick={() => setServicesOpen(false)}
                    >
                      <p className="font-medium text-base text-primary">For Individuals</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Life coaching, personal branding & growth</p>
                    </Link>
                    <Link
                      to="/services/companies"
                      className="group p-2.5 rounded-md hover:bg-muted transition-colors"
                      onClick={() => setServicesOpen(false)}
                    >
                      <p className="font-medium text-base text-primary">For Organisations</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Executive facilitation & training</p>
                    </Link>
                    <Link
                      to="/services/students"
                      className="group p-2.5 rounded-md hover:bg-muted transition-colors"
                      onClick={() => setServicesOpen(false)}
                    >
                      <p className="font-medium text-base text-primary">For Students</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Campus-to-corporate readiness</p>
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {navLinks.slice(2).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-2.5 py-2 text-sm lg:text-base font-medium text-primary hover:text-primary/70 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-full hover:bg-muted transition-colors text-foreground/70"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </button>
            <DiscoveryCallDialog>
              <Button
                size="sm"
                className="hidden sm:inline-flex bg-accent text-accent-foreground hover:bg-accent/90 font-semibold"
              >
                Book a Discovery Call
              </Button>
            </DiscoveryCallDialog>
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-full hover:bg-muted transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-primary/80 backdrop-blur-sm flex items-start justify-center pt-32"
            onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
          >
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-xl mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search MVIBE..."
                  className="w-full h-12 pl-12 pr-12 rounded-xl bg-card text-foreground text-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button
                  onClick={() => { setSearchOpen(false); setSearchQuery(""); }}
                  aria-label="Close search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Search Results */}
              {searchQuery.trim() && (
                <div className="mt-2 bg-card rounded-xl shadow-lg border border-border max-h-80 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <p className="p-4 text-muted-foreground text-base">No results found.</p>
                  ) : (
                    searchResults.map((r, i) => (
                      <button
                        key={`${r.url}-${i}`}
                        onClick={() => handleResultClick(r.url)}
                        className="w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-b-0 flex items-start gap-3"
                      >
                        <span className="text-xs font-semibold uppercase tracking-wide text-primary bg-primary/10 px-2 py-0.5 rounded mt-0.5 shrink-0">{r.type}</span>
                        <div>
                          <p className="font-medium text-foreground text-base">{r.title}</p>
                          <p className="text-sm text-muted-foreground line-clamp-1">{r.description}</p>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-50 bg-card flex flex-col"
          >
            <div className="flex items-center justify-between h-24 px-4">
              <LogoBrand />
              <button
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-full hover:bg-muted"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-4 mt-6">
              <Link to="/" onClick={handleLogoClick} className="py-2.5 text-lg font-medium text-foreground/80 hover:text-primary border-b border-border">Home</Link>
              <Link to="/about" onClick={() => setMobileOpen(false)} className="py-2.5 text-lg font-medium text-foreground/80 hover:text-primary border-b border-border">About Us</Link>
              <Link to="/services" onClick={() => setMobileOpen(false)} className="py-2.5 text-lg font-medium text-foreground/80 hover:text-primary border-b border-border">Services</Link>
              <div className="pl-4 pb-2 flex flex-col gap-1.5 border-b border-border">
                <Link to="/services/individuals" onClick={() => setMobileOpen(false)} className="text-base text-muted-foreground hover:text-primary">For Individuals</Link>
                <Link to="/services/companies" onClick={() => setMobileOpen(false)} className="text-base text-muted-foreground hover:text-primary">For Organisations</Link>
                <Link to="/services/students" onClick={() => setMobileOpen(false)} className="text-base text-muted-foreground hover:text-primary">For Students</Link>
              </div>
              {navLinks.slice(2).map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="py-2.5 text-lg font-medium text-foreground/80 hover:text-primary border-b border-border">{l.label}</Link>
              ))}
            </nav>
            <div className="px-4 mt-6">
              <DiscoveryCallDialog>
                <Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold h-11 text-base" onClick={() => setMobileOpen(false)}>
                  Book a Discovery Call
                </Button>
              </DiscoveryCallDialog>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
